import { useId, useState } from 'react'
import { Mtds, DateInputWidget } from 'prove-common'

type FilterSelectProps = {
  'data-cy'?: string
  isError?: boolean
  isLoading?: boolean
  name: string
  onOpenChange?: (isOpen: boolean) => void
  options: Array<{ id: string; name: string }>
  placeholder?: string
  selectedIds: string[]
  selectionMode?: 'single' | 'multiple' | 'date'
  setSelectedIds: (ids: string[]) => void
  searchAriaLabel?: string
  errorMessage?: string
  allOptionLabel?: string
  fromDateLabel?: string
  toDateLabel?: string
  dateRangeErrorMessage?: string
  fromDate?: string
  toDate?: string
  setFromDate?: (date: string) => void
  setToDate?: (date: string) => void
}

export function FilterSelect({
  'data-cy': cy,
  isError,
  isLoading,
  name,
  onOpenChange,
  options,
  placeholder = 'Søk',
  selectedIds,
  selectionMode = 'multiple',
  setSelectedIds,
  searchAriaLabel = 'Søk',
  errorMessage = 'Det oppstod en feil.',
  allOptionLabel = 'Alle',
  fromDateLabel = 'Fra',
  toDateLabel = 'Til',
  dateRangeErrorMessage = 'Fra dato kan ikke være senere enn til dato',
  fromDate = '',
  toDate = '',
  setFromDate,
  setToDate
}: Readonly<FilterSelectProps>) {
  const popId = useId()
  const [searchText, setSearchText] = useState('')
  const filteredOptions = options.filter((option) =>
    option.name?.toLowerCase().includes(searchText.toLowerCase())
  )

  function handleDateChange(type: 'from' | 'to', value: string) {
    const newFromDate = type === 'from' ? value : fromDate
    const newToDate = type === 'to' ? value : toDate

    if (newFromDate && newToDate && newFromDate > newToDate) {
      Mtds.toast.danger(dateRangeErrorMessage)
      return
    }

    if (type === 'from') setFromDate?.(value)
    else setToDate?.(value)
  }

  return (
    <>
      <Mtds.Button data-arrow data-cy={cy} popoverTarget={popId}>
        {name}
        {selectionMode !== 'date' && !!selectedIds.length && (
          <Mtds.Badge data-badge={selectedIds.length} />
        )}
        {selectionMode === 'date' && (fromDate || toDate) && (
          <Mtds.Badge data-badge={1} />
        )}
      </Mtds.Button>
      <Mtds.Popover
        data-overscroll='contain'
        id={popId}
        onToggle={(e) => onOpenChange?.(e.newState === 'open')}
      >
        <Mtds.Fieldset>
          <legend>{name}</legend>
          {selectionMode !== 'date' && (
            <Mtds.Input
              aria-label={searchAriaLabel}
              onChange={({ target }) => setSearchText(target.value)}
              placeholder={placeholder}
              type='search'
              value={searchText}
            />
          )}
          {isLoading === true && <Mtds.Spinner />}
          {isError === true && <p>{errorMessage}</p>}
          {selectionMode === 'single' &&
            [{ name: allOptionLabel, id: '' }, ...filteredOptions].map(
              ({ name, id }, index) => (
                <Mtds.Field
                  as='input'
                  type='radio'
                  key={`${name}-${id}`}
                  label={name}
                  value={name}
                  checked={index ? id === selectedIds[0] : !selectedIds.length}
                  onChange={() =>
                    setSelectedIds(id == '' || id == null ? [] : [id])
                  }
                />
              )
            )}
          {selectionMode === 'multiple' &&
            filteredOptions.map(({ name, id }) => (
              <Mtds.Field
                as='input'
                type='checkbox'
                key={name}
                label={name}
                value={name}
                checked={selectedIds.includes(id)}
                onChange={({ target: { checked } }) => {
                  if (checked) setSelectedIds([...selectedIds, id])
                  else setSelectedIds(selectedIds.filter((sid) => sid !== id))
                }}
              />
            ))}
          {selectionMode === 'date' && (
            <Mtds.Grid data-cols='2' data-gap={8}>
              <Mtds.Field>
                <label htmlFor={`${popId}-from`}>{fromDateLabel}</label>
                <DateInputWidget
                  id={`${popId}-from`}
                  value={fromDate?.slice(0, 10) ?? ''}
                  onChange={(value) => handleDateChange('from', value)}
                />
              </Mtds.Field>
              <Mtds.Field>
                <label htmlFor={`${popId}-to`}>{toDateLabel}</label>
                <DateInputWidget
                  id={`${popId}-to`}
                  value={toDate?.slice(0, 10) ?? ''}
                  onChange={(value) => handleDateChange('to', value)}
                />
              </Mtds.Field>
            </Mtds.Grid>
          )}
        </Mtds.Fieldset>
      </Mtds.Popover>
    </>
  )
}
